/**
 * The account's screens (part 36a; `cockatrice-ui`: `Onboarding.jsx` and `TrialEnded.jsx`, with the cards the design
 * lacks built in the gate's look, the user's choice 2026-10-23): the gate a first start shows (welcome, create an
 * account, log in), "check your email", the verify link, a login's second step; the banner saying why the books are
 * read-only (no account, the trial ended, no copy confirmed) or that the relay logged this device out; the header's
 * trial chip; the writer's read-only note. Tables AC, SU, LG, RO; accessibility as the part log's list.
 */
import { createMemo, createSignal, untrack } from 'solid-js';
import type { Accessor } from 'solid-js';
import { canonicalEmail } from '../account/email.ts';
import type { SecondProof } from '../account/flows.ts';
import { askPasskey } from '../account/passkey.boundary.ts';
import type { PasskeyRequest, SecondFactorNeeded } from '../account/wire.ts';
import { LEGAL_VERSIONS } from '../account/legal.ts';
import Badge from '../ui/Badge.ts';
import Banner from '../ui/Banner.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import Icon from '../ui/Icon.ts';
import type { IconName } from '../ui/Icon.ts';
import TextField from '../ui/TextField.ts';
import { component, el, when, whenSome } from '../ui/solid.ts';
import { Back, EmailField, ErrorText, focusField, Frame, Heading, problemWords, Submit } from './account-fields.ts';
import { ForgotCard, LinkCard } from './account-links.ts';
import { SheetNewPassword, SheetPassword } from './account-sheet.ts';

export { problemWords, waitWords } from './account-fields.ts';
import type { Child } from '../ui/solid.ts';
import type { Shell } from './shell.ts';
import { isGuest } from '../account/status.ts';

/** A card of the gate (table AC; SU, LG); from part 36c the reset's (RQ) and the mailed links' cards (LK). */
export type AccountStep = 'welcome' | 'create' | 'login' | 'check-email' | 'verify' | 'second' | 'forgot' | 'reset' | 'confirm' | 'enrol' | 'keep';

/** AC2, AC9: the card the gate shows now, or null when it is closed. */
export function gateStep(shell: Shell): AccountStep | null {
  const step = shell.ui.accountStep();
  if (step !== null) return step;
  const status = shell.account.status();
  // Part 38c, JL1: an invite link waiting and no account here: its Create card, in the invite's words.
  if (status.kind === 'none' && status.gate && invited(shell)) return 'create';
  return status.kind === 'none' && status.gate ? 'welcome' : null;
}

/** A point of the welcome card: its mark and its words. */
function Point(icon: IconName, strong: string, rest: string): Child {
  return el('li', {}, el('span', { class: 'ac-points__mark' }, component(Icon, { name: icon })), el('span', {}, el('strong', {}, strong), ` ${rest}`));
}

/** A link in a card's sentence ("Log in", "Not now"): the library's link Button. */
function TextLink(label: string, onClick: () => void, className?: string): Child {
  return component(Button, { variant: 'link', class: className, onClick, children: label });
}

/** A note under a card's form: the info mark and its words. */
function Note(words: string): Child {
  return el('p', { class: 'ac-note' }, component(Icon, { name: 'info', size: 'sm' }), el('span', {}, words));
}

/** A card's large full-width button (the library's), `variant` as the design draws it. */
function Wide(label: string, onClick: () => void, variant: 'primary' | 'secondary' | 'ghost', className?: string): Child {
  return component(Button, { variant, size: 'lg', fullWidth: true, class: className, onClick, children: label });
}

/** WL: the welcome card (the design's Welcome), with "Not now" (AC3, the user's choice 2026-10-23). */
function Welcome(shell: Shell): Child {
  const { t, account, ui } = shell;
  return el(
    'section',
    { class: 'ac-card ac-welcome', 'aria-labelledby': 'ob-title' },
    Heading('ob-title', t('account.welcome.title'), 'ac-welcome__title'),
    el('p', { class: 'ac-lead' }, t('account.welcome.lead')),
    el('ul', { class: 'ac-points' }, Point('lock', t('account.welcome.local'), t('account.welcome.localRest')), Point('check', t('account.welcome.trial'), t('account.welcome.trialRest'))),
    el(
      'div',
      { class: 'ac-actions ac-actions--stack' },
      Wide(t('account.welcome.start'), () => ui.setAccountStep('create'), 'primary', 'start-trial'),
      Wide(t('account.welcome.have'), () => ui.setAccountStep('login'), 'ghost', 'have-account'),
    ),
    el(
      'p',
      { class: 'ac-muted ac-center ac-not-now' },
      TextLink(
        t('account.welcome.notNow'),
        () => {
          ui.setAccountStep(null);
          void account.notNow();
        },
        'not-now',
      ),
      ` ${t('account.welcome.notNowRest')}`,
    ),
  );
}

/** Where a gate card goes back to: the welcome while the gate stands (AC2), else the gate closes (opened from a banner). */
function back(shell: Shell): () => void {
  return () => {
    const status = shell.account.status();
    shell.ui.setAccountStep(status.kind === 'none' && status.gate ? 'welcome' : null);
  };
}

/** Part 38c, JL1, JL2: whether an invite link waits on this device (the cards then say so, and a signup is a guest's). */
function invited(shell: Shell): boolean {
  return shell.shared.invite().kind !== 'none';
}

/** SU1–SU5: create an account (the design's Create). */
function Create(shell: Shell, remember: (email: string) => void): Child {
  const { t, account, ui } = shell;
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [agreed, setAgreed] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [reload, setReload] = createSignal(false);
  // SU3: the library's field, its meter scored by the account's rule; the scorer is fetched at the first character (its
  // dictionaries stay off the start).
  const strong = SheetNewPassword(shell, 'cr-pw', email, setPassword, t('account.password'), 'cr-error');
  const site = account.site();
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (busy()) return;
    const address = canonicalEmail(email());
    setReload(false);
    if (address === null) {
      setError(t('account.error.email'));
      focusField('cr-email');
      return;
    }
    // SU3: a password is never called weak because its scorer hadn't loaded: the submit waits for it (busy meanwhile), and
    // a scorer that can't load is the connection's problem, said as such. An empty password is a weak one here.
    setBusy(true);
    const said = await strong.check();
    setBusy(false);
    const failed = problemWords(shell, { kind: 'failed' });
    if (said === failed) {
      setError(failed);
      return;
    }
    if (said !== null) {
      setError(t('account.error.weak'));
      focusField('cr-pw');
      return;
    }
    if (!agreed()) {
      setError(t('account.error.agree'));
      focusField('cr-agree');
      return;
    }
    setBusy(true);
    setError('');
    // Part 38c, JL1: with an invite waiting, a guest's account (no trial clock; a trial can start later, IN18).
    const outcome = await account.signUp(address, password(), LEGAL_VERSIONS, invited(shell));
    setBusy(false);
    if (outcome.kind === 'check-email') {
      remember(address);
      ui.setAccountStep('check-email');
      return;
    }
    setError(problemWords(shell, outcome));
    setReload(outcome.kind === 'refused' && outcome.error === 'terms-outdated');
  };
  return el(
    'form',
    { class: 'ac-card ac-form create-account', 'aria-labelledby': 'cr-title', noValidate: true, onSubmit: (event: SubmitEvent) => void submit(event) },
    Back(shell, back(shell)),
    Heading('cr-title', invited(shell) ? t('join.create.title') : t('account.create.title'), 'ac-form__title'),
    el('p', { class: 'ac-muted' }, invited(shell) ? t('join.create.lead') : t('account.create.lead')),
    EmailField(shell, 'cr-email', 'email', email, setEmail, 'cr-error'),
    strong.field,
    component(Checkbox, {
      id: 'cr-agree',
      'aria-describedby': 'cr-error',
      get checked() {
        return agreed();
      },
      onChange: (checked: boolean) => {
        setAgreed(checked);
      },
      label: [
        `${t('account.create.agreeStart')} `,
        el('a', { href: `${site}/terms/`, target: '_blank', rel: 'noopener noreferrer' }, t('account.create.terms')),
        ` ${t('account.create.agreeMiddle')} `,
        el('a', { href: `${site}/privacy/`, target: '_blank', rel: 'noopener noreferrer' }, t('account.create.privacy')),
        t('account.create.agreeEnd'),
      ],
    }),
    ErrorText('cr-error', error),
    when(reload, () => component(Button, { onClick: () => { shell.reload(); }, children: t('account.reload') })),
    Submit(shell, invited(shell) ? t('join.create.submit') : t('account.create.submit'), busy, { off: () => !agreed() }),
    el('p', { class: 'ac-muted ac-center' }, `${t('account.create.have')} `, TextLink(t('account.login.submit'), () => ui.setAccountStep('login'))),
  );
}

/** LG1–LG12: log in (the design's Log in); its second step is {@link Second}'s. */
function Login(shell: Shell, remember: (email: string) => void, need: (need: SecondFactorNeeded) => void): Child {
  const { t, account, ui } = shell;
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (busy()) return;
    const address = canonicalEmail(email());
    if (address === null || password() === '') {
      setError(address === null ? t('account.error.email') : t('account.error.password'));
      focusField(address === null ? 'li-email' : 'li-pw');
      return;
    }
    setBusy(true);
    setError('');
    const outcome = await account.logIn(address, password());
    setBusy(false);
    if (outcome.kind === 'done') {
      ui.setAccountStep(null);
      return;
    }
    if (outcome.kind === 'second-factor') {
      remember(address);
      need(outcome.need);
      ui.setAccountStep('second');
      return;
    }
    setError(problemWords(shell, outcome));
    focusField('li-pw');
  };
  return el(
    'form',
    { class: 'ac-card ac-form log-in', 'aria-labelledby': 'li-title', noValidate: true, onSubmit: (event: SubmitEvent) => void submit(event) },
    Back(shell, back(shell)),
    Heading('li-title', t('account.login.title'), 'ac-form__title'),
    el('p', { class: 'ac-muted' }, invited(shell) ? t('join.login.lead') : t('account.login.lead')),
    EmailField(shell, 'li-email', 'username', email, setEmail, 'li-error'),
    // Part 36c, RQ1: the reset by E, on the password's label line (before the field, so Tab still meets it first).
    el('div', { class: 'ac-pw-forgot' }, TextLink(t('account.login.forgot'), () => ui.setAccountStep('forgot'), 'forgot-password'), SheetPassword(shell, 'li-pw', 'current-password', setPassword, t('account.password'), 'li-error')),
    ErrorText('li-error', error),
    Submit(shell, t('account.login.submit'), busy),
    Note(t('account.login.note')),
    el('p', { class: 'ac-muted ac-center' }, `${t('account.login.new')} `, TextLink(t('account.login.create'), () => ui.setAccountStep('create'))),
  );
}

/** SU1: "check your email", the same for every address. */
function CheckEmail(shell: Shell, email: Accessor<string>): Child {
  const { t } = shell;
  return el(
    'section',
    { class: 'ac-card ac-form check-email', 'aria-labelledby': 'ce-title' },
    Heading('ce-title', t('account.check.title'), 'ac-form__title'),
    el('p', { class: 'ac-lead' }, () => t('account.check.lead', { email: email() })),
    el('p', { class: 'ac-muted' }, t('account.check.rest')),
    Wide(t('account.back'), back(shell), 'secondary'),
  );
}

/** SU6–SU8, AC9: the verify link: the account's address and password complete it on this device. */
function Verify(shell: Shell, remembered: Accessor<string>): Child {
  const { t, account, ui } = shell;
  const [email, setEmail] = createSignal(remembered());
  const [password, setPassword] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  // SU8: a link that holds no token is said at once.
  const [error, setError] = createSignal(ui.verifyToken() === '' ? t('account.error.link') : '');
  const close = (): void => {
    ui.setVerifyToken(null);
    ui.setAccountStep(null);
  };
  const other = (): string | null => {
    const status = account.status();
    return status.kind === 'account' && !status.signedOut ? status.email : null;
  };
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    const token = ui.verifyToken();
    const address = canonicalEmail(email());
    if (busy() || token === null) return;
    if (token === '') {
      setError(t('account.error.link'));
      return;
    }
    if (address === null || password() === '') {
      setError(address === null ? t('account.error.email') : t('account.error.password'));
      focusField(address === null ? 'vf-email' : 'vf-pw');
      return;
    }
    setBusy(true);
    setError('');
    const outcome = await account.verify(token, address, password());
    setBusy(false);
    if (outcome.kind === 'done') {
      close();
      return;
    }
    if (outcome.kind === 'wrong-address') {
      ui.setVerifyToken(null);
      ui.setAccountStep('login');
      return;
    }
    setError(problemWords(shell, outcome));
    focusField('vf-pw');
  };
  return whenSome(
    other,
    (email) =>
      el(
        'section',
        { class: 'ac-card ac-form verify-other', 'aria-labelledby': 'vo-title' },
        Heading('vo-title', t('account.verify.title'), 'ac-form__title'),
        el('p', { class: 'ac-lead' }, () => t('account.verify.other', { email: email() })),
        Wide(t('account.verify.keep'), close, 'secondary'),
      ),
    () =>
      el(
        'form',
        { class: 'ac-card ac-form verify-link', 'aria-labelledby': 'vf-title', noValidate: true, onSubmit: (event: SubmitEvent) => void submit(event) },
        Heading('vf-title', t('account.verify.title'), 'ac-form__title'),
        el('p', { class: 'ac-muted' }, t('account.verify.lead')),
        // VL5: on iPhone or iPad outside the Home Screen app, this browser's storage isn't the app's: log in there too.
        when(
          () => shell.homeScreen.state() !== 'none',
          () => Note(t('account.verify.homeScreen')),
        ),
        EmailField(shell, 'vf-email', 'username', email, setEmail, 'vf-error'),
        SheetPassword(shell, 'vf-pw', 'current-password', setPassword, t('account.password'), 'vf-error'),
        ErrorText('vf-error', error),
        Submit(shell, t('account.verify.submit'), busy),
        el('p', { class: 'ac-muted ac-center' }, TextLink(t('account.verify.later'), close)),
      ),
  );
}

/** LG5, LG9, LG10: a login's second step: a passkey, a code from the app, or a backup code. */
function Second(shell: Shell, need: Accessor<SecondFactorNeeded | null>): Child {
  const { t, account, ui } = shell;
  const [code, setCode] = createSignal('');
  const [backup, setBackup] = createSignal(need()?.totp !== true);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [left, setLeft] = createSignal<number | null>(null);
  const prove = async (proof: SecondProof): Promise<void> => {
    const ticket = need()?.ticket;
    if (busy() || ticket === undefined) return;
    setBusy(true);
    setError('');
    const outcome = await account.secondStep(ticket, proof);
    setBusy(false);
    if (outcome.kind === 'done') {
      // LG5: backup codes left are said once logged in, when one was used.
      if (outcome.backupCodesLeft === null) ui.setAccountStep(null);
      else setLeft(outcome.backupCodesLeft);
      return;
    }
    if (outcome.kind === 'refused' && outcome.error === 'ticket-invalid') {
      ui.setAccountStep('login');
      return;
    }
    setError(problemWords(shell, outcome));
    focusField('sf-code');
  };
  const submit = (event: SubmitEvent): void => {
    event.preventDefault();
    const typed = code().trim();
    if (typed === '') {
      setError(t('account.error.noCode'));
      focusField('sf-code');
      return;
    }
    void prove(backup() ? { backupCode: typed } : { totp: typed.replace(/\s/gu, '') });
  };
  const passkey = async (request: PasskeyRequest): Promise<void> => {
    const answer = await askPasskey(shell.credentials, request);
    if (answer === null) setError(t('account.error.passkey'));
    else await prove({ passkey: answer });
  };
  return whenSome(
    left,
    (count) =>
      el(
        'section',
        { class: 'ac-card ac-form codes-left', 'aria-labelledby': 'cl-title' },
        Heading('cl-title', t('account.second.used'), 'ac-form__title'),
        el('p', { class: 'ac-lead' }, () => t('account.second.left', { count: count() })),
        Wide(t('account.second.continue'), () => ui.setAccountStep(null), 'primary'),
      ),
    () =>
      el(
        'form',
        { class: 'ac-card ac-form second-step', 'aria-labelledby': 'sf-title', noValidate: true, onSubmit: submit },
        Back(shell, () => ui.setAccountStep('login')),
        Heading('sf-title', t('account.second.title'), 'ac-form__title'),
        el('p', { class: 'ac-muted' }, t('account.second.lead')),
        whenSome(
          () => need()?.passkey,
          (request) =>
            component(Button, {
              size: 'lg',
              fullWidth: true,
              class: 'use-passkey',
              get 'aria-disabled'() {
                return busy() ? 'true' : 'false';
              },
              onClick: () => void passkey(request()),
              children: t('account.second.passkey'),
            }),
        ),
        // A backup code has letters and dashes: a text field, what is typed sent as typed (less spaces for the app's).
        component(TextField, {
          id: 'sf-code',
          label: () => (backup() ? t('account.second.backupLabel') : t('account.second.codeLabel')),
          autocomplete: 'one-time-code',
          inputMode: () => (backup() ? 'text' : 'numeric'),
          get value() {
            return code();
          },
          'aria-describedby': 'sf-error',
          onChange: (value: string) => {
            setCode(value);
          },
        }),
        ErrorText('sf-error', error),
        Submit(shell, t('account.login.submit'), busy),
        when(
          () => {
            const now = need();
            return now?.totp === true && now.backupCodes > 0;
          },
          () =>
            el(
              'p',
              { class: 'ac-muted ac-center' },
              component(Button, {
                variant: 'link',
                class: 'switch-code',
                onClick: () => {
                  setBackup(!backup());
                  setCode('');
                  setError('');
                },
                children: () => (backup() ? t('account.second.useApp') : t('account.second.useBackup')),
              }),
            ),
        ),
      ),
  );
}

/** The gate (table AC): the card of the step, over the whole page while it shows. */
export function AccountGate(shell: Shell): Child {
  const [email, setEmail] = createSignal('');
  const [need, setNeed] = createSignal<SecondFactorNeeded | null>(null);
  // Each card is made again only when the step changes (or, for the verify card, another link arrives), and untracked
  // (as a component is), so nothing else its making reads makes it again under the writer's typing.
  // Part 36c, LK: so does another link of the kind arriving while its card is open (the verify link's token, or another's).
  const step = createMemo(
    () => {
      const now = gateStep(shell);
      return { now, link: now === 'verify' ? shell.ui.verifyToken() : shell.ui.linkToken() };
    },
    undefined,
    { equals: (a, b) => a.now === b.now && a.link === b.link },
  );
  const made = (now: AccountStep | null): Child => {
    switch (now) {
      case 'create':
        return Create(shell, setEmail);
      case 'login':
        return Login(shell, setEmail, setNeed);
      case 'check-email':
        return CheckEmail(shell, email);
      case 'verify':
        return Verify(shell, email);
      case 'second':
        return Second(shell, need);
      case 'forgot':
        return ForgotCard(shell);
      case 'reset':
      case 'confirm':
      case 'enrol':
      case 'keep':
        return LinkCard(shell, now);
      case 'welcome':
      case null:
        return Welcome(shell);
    }
  };
  // Made once per step (a memo): the Card's body may run its insertion again, and must not make a filled card anew.
  const card = createMemo((): Child => {
    const { now } = step();
    return untrack(() => made(now));
  });
  return Frame(shell, card);
}

/** A banner's words, its mark and its buttons. */
interface BannerContent {
  readonly kind: 'none' | 'logged-out' | 'ended' | 'unknown' | 'signed-out';
  readonly warn: boolean;
}

/**
 * C1, C8, AC3, AC6, AC7, ST4: what the banner says now: why the books are read-only (no account; the trial ended; no copy
 * confirmed yet), or that the relay logged this device out while it may still write; null for nothing to say.
 */
export function bannerContent(shell: Shell): BannerContent | null {
  const { account } = shell;
  const status = account.status();
  // BK2: with nobody logged in on a claimed device the library is empty: the banner says why, not that books can be read.
  if (status.kind === 'none') return status.gate ? null : { kind: account.shelf()?.kind === 'none' ? 'logged-out' : 'none', warn: false };
  if (status.kind !== 'account') return null;
  // Part 38c (decision 75): a guest never had a trial to end: Shared with me says what it can do (start one, IN18).
  if (isGuest(status)) return status.signedOut ? { kind: 'signed-out', warn: false } : null;
  const standing = account.standing();
  if (standing.kind === 'ended') return { kind: 'ended', warn: true };
  if (standing.kind === 'unknown') return { kind: 'unknown', warn: true };
  return status.signedOut ? { kind: 'signed-out', warn: false } : null;
}

/** The banner (the design's trial-ended banner, and its kin; the library's strip Banner): at the top of the home page and the writer. */
export function AccountBanner(shell: Shell): Child {
  const { t, account, ui, locale } = shell;
  const date = (at: number): string => new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(at);
  const purge = (): string | null => {
    const standing = account.standing();
    // Part 38a (table GC): an account that began as a guest keeps itself and the books shared with it.
    if (standing.kind === 'ended' && standing.sharesEndAt !== null) return t('account.banner.shares', { date: date(standing.sharesEndAt) });
    return standing.kind === 'ended' && standing.purgeAt !== null ? t('account.banner.purge', { date: date(standing.purgeAt) }) : null;
  };
  const signedOut = (): boolean => {
    const status = account.status();
    return status.kind === 'account' && status.signedOut;
  };
  const logIn = (): Child => component(Button, { size: 'sm', class: 'banner-log-in', onClick: () => ui.setAccountStep('login'), children: t('account.login.submit') });
  return whenSome(
    () => bannerContent(shell),
    (content) => {
      // The buttons, made once for what the banner says (the Banner reads them to know it has any, then shows them).
      const actions = createMemo((): Child => {
        switch (content().kind) {
          case 'none':
          case 'logged-out':
            return [component(Button, { variant: 'primary', size: 'sm', class: 'banner-start', onClick: () => ui.setAccountStep('create'), children: t('account.welcome.start') }), logIn()];
          case 'ended':
            // The checkout is fix-next 56's: Buy shows as what's coming (decision 77).
            return [component(Button, { variant: 'primary', size: 'sm', 'aria-disabled': 'true', children: t('home.laterName', { name: t('account.buy') }) }), signedOut() ? logIn() : null];
          case 'unknown':
          case 'signed-out':
            return signedOut() ? logIn() : null;
        }
      });
      return component(Banner, {
        strip: true,
        // The design's info mark, in the warning's colour when it warns.
        icon: 'info',
        get tone() {
          return content().warn ? 'warn' : 'info';
        },
        class: 'account-banner',
        role: 'region',
        'aria-label': t('account.banner.label'),
        get title() {
          return t(`account.banner.${content().kind}.title`);
        },
        children: [
          () => t(`account.banner.${content().kind}.text`),
          whenSome(purge, (words) => el('span', { class: 'ac-banner__sub' }, words)),
          when(
            () => content().kind !== 'signed-out' && signedOut(),
            () => el('span', { class: 'ac-banner__sub' }, t('account.banner.alsoSignedOut')),
          ),
        ],
        get actions() {
          return actions();
        },
      });
    },
  );
}

/** The header's trial chip (the design's "Trial · 7 days left"; the library's Badge): only in a trial. */
export function TrialChip(shell: Shell): Child {
  return whenSome(
    () => {
      const standing = shell.account.standing();
      return standing.kind === 'trial' ? standing.daysLeft : null;
    },
    (days) => component(Badge, { size: 'lg', class: 'ac-trial-chip', children: () => shell.t('account.chip', { count: days() }) }),
  );
}

/** The writer's note while the books are read-only (the design's paused note; the library's Banner), with the reason the banner gives. */
export function ReadOnlyNote(shell: Shell): Child {
  return when(
    () => shell.workspace.editable() && !shell.workspace.writable(),
    () =>
      component(Banner, {
        tone: 'neutral',
        icon: 'lock',
        class: 'ac-paused read-only-note',
        children: shell.t('account.paused'),
        // The design's "Buy to keep writing": the checkout is fix-next 56's (decision 77).
        actions: component(Button, { size: 'sm', 'aria-disabled': 'true', children: shell.t('home.laterName', { name: shell.t('account.buyToWrite') }) }),
      }),
  );
}
